import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Box, Heading, Text, Link as ChakraLink } from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { useAdminDocsHost } from './host-context';
import { manifest } from './manifest.generated';
import type { DocManifestEntry } from './types';

export interface DocsViewerProps {
  entry: DocManifestEntry;
  body: string;
}

const isExternal = (href: string | undefined) =>
  !!href && /^([a-z]+:)?\/\//i.test(href);

/**
 * Slugs actually in this bundle. The manifest carries EVERY guide, both
 * audiences, so membership here is the honest answer to "can this viewer
 * render that link" — and it is the only thing standing between a
 * directory-qualified href and a route that 404s (see below).
 */
const BUNDLED_SLUGS: ReadonlySet<string> = new Set(manifest.map((e) => e.slug));

/** Shape of a guide slug. Mirrors SLUG_PATTERN in tools/admin-docs/sync.mjs. */
const SLUG_SEGMENT = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/**
 * If `href` names a guide carried in this bundle, return its slug.
 *
 * The in-app library is FLAT — keyed by slug, with no directory structure —
 * but the guides it is generated from are not: they may sit in subdirectories,
 * and a cross-link between a top-level guide and a nested one has to carry a
 * directory segment (`./customer-onboarding/relay-agent-install.md`,
 * `../permissions.md`). A bare-slug pattern would resolve every such link to
 * null and render it as plain text on /account/docs — no 404 and no broken
 * href, so nothing reports it. Resolution is therefore by BASENAME: whatever
 * directory the href walks through, the file it lands on is a guide iff its
 * name is a slug in the bundle.
 *
 * Bundle membership is doing real work, not decoration. Plenty of guide links
 * point at repo files that are not guides at all
 * (`../../adr/067-B-rtsp-relay-tier.md`), and their basenames are shaped
 * exactly like slugs — resolving those by pattern alone would turn today's
 * harmless plain text into an in-app route that 404s. Unknown basename ->
 * null -> `isOutOfBundleMdHref` unwraps it, the same safe plain-text fallback
 * every non-guide link gets.
 *
 * Still rejected: absolute paths, http(s) URLs, other schemes, and non-.md
 * extensions. `knownSlugs` is injectable for tests; production reads the
 * generated manifest.
 */
export function inBundleSlugFromHref(
  href: string | undefined,
  knownSlugs: ReadonlySet<string> = BUNDLED_SLUGS,
): string | null {
  if (!href) return null;
  if (isExternal(href) || href.startsWith('/')) return null;
  const path = href.split('#')[0];
  if (!path.endsWith('.md')) return null;
  const basename = path.slice(0, -'.md'.length).split('/').pop() ?? '';
  if (!SLUG_SEGMENT.test(basename)) return null;
  return knownSlugs.has(basename) ? basename : null;
}

/** True when the href is a `.md` reference we can't resolve in-bundle. */
function isOutOfBundleMdHref(href: string | undefined): boolean {
  if (!href || isExternal(href)) return false;
  if (!/\.md(?:#|$)/.test(href)) return false;
  return inBundleSlugFromHref(href) === null;
}

export function DocsViewer({ entry, body }: DocsViewerProps) {
  const { resolveDocUrl } = useAdminDocsHost();

  return (
    <Box maxW="container.md" mx="auto" px="4" py="6">
      <Heading as="h1" size="lg" mb="2">
        {entry.title}
      </Heading>
      {entry.summary && (
        <Text color="neutral.600" mb="6">
          {entry.summary}
        </Text>
      )}
      <Box>
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            h1: ({ children }) => (
              <Heading as="h2" size="md" mt="6" mb="2">
                {children}
              </Heading>
            ),
            h2: ({ children }) => (
              <Heading as="h3" size="sm" mt="5" mb="2">
                {children}
              </Heading>
            ),
            h3: ({ children }) => (
              <Heading as="h4" size="xs" mt="4" mb="2">
                {children}
              </Heading>
            ),
            p: ({ children }) => (
              <Text mb="3" color="neutral.700" lineHeight="tall">
                {children}
              </Text>
            ),
            ul: ({ children }) => (
              <Box as="ul" pl="6" mb="3">
                {children}
              </Box>
            ),
            ol: ({ children }) => (
              <Box as="ol" pl="6" mb="3">
                {children}
              </Box>
            ),
            li: ({ children }) => (
              <Box as="li" mb="1" color="neutral.700">
                {children}
              </Box>
            ),
            a: ({ href, children }) => {
              const inBundleSlug = inBundleSlugFromHref(href);
              if (inBundleSlug && resolveDocUrl) {
                return (
                  <ChakraLink asChild color="blue.600">
                    <RouterLink to={resolveDocUrl(inBundleSlug)}>{children}</RouterLink>
                  </ChakraLink>
                );
              }
              if (isExternal(href)) {
                return (
                  <ChakraLink href={href} target="_blank" rel="noopener noreferrer" color="blue.600">
                    {children}
                  </ChakraLink>
                );
              }
              if (isOutOfBundleMdHref(href)) {
                // Don't render a broken anchor or an in-app route that 404s.
                // The reference is to content not bundled here; show the
                // link text only so the surrounding prose still reads.
                return <>{children}</>;
              }
              return (
                <ChakraLink href={href} color="blue.600">
                  {children}
                </ChakraLink>
              );
            },
            code: ({ children }) => (
              <Box
                as="code"
                bg="neutral.100"
                px="1"
                py="0.5"
                borderRadius="sm"
                fontFamily="mono"
                fontSize="sm"
              >
                {children}
              </Box>
            ),
            pre: ({ children }) => (
              <Box
                as="pre"
                bg="neutral.100"
                p="3"
                borderRadius="md"
                overflow="auto"
                mb="3"
                fontFamily="mono"
                fontSize="sm"
              >
                {children}
              </Box>
            ),
            blockquote: ({ children }) => (
              <Box
                as="blockquote"
                borderLeftWidth="4px"
                borderColor="neutral.300"
                pl="4"
                py="1"
                my="3"
                color="neutral.600"
              >
                {children}
              </Box>
            ),
            table: ({ children }) => (
              <Box overflowX="auto" mb="3">
                <Box as="table" w="full" borderCollapse="collapse">
                  {children}
                </Box>
              </Box>
            ),
            th: ({ children }) => (
              <Box
                as="th"
                textAlign="left"
                p="2"
                borderBottomWidth="1px"
                borderColor="neutral.200"
                fontWeight="semibold"
              >
                {children}
              </Box>
            ),
            td: ({ children }) => (
              <Box as="td" p="2" borderBottomWidth="1px" borderColor="neutral.100">
                {children}
              </Box>
            ),
          }}
        >
          {body}
        </ReactMarkdown>
      </Box>
    </Box>
  );
}
